import { IPromise } from 'angular';

import { Messages } from '../../codegen/Localize';
import { ICreateOrEditScheduleScope, IScheduleViewModel } from '../controllers/CreateOrEditScheduleCtrl';
import { CreateOrEditScheduleViewModelMapper } from '../controllers/CreateOrEditScheduleViewModelMapper';
import { IActionContext } from '../helpers/ActionContext';
import { L10n } from '../L10n/L10n';
import { Module } from '../Module';
import { Schedules } from '../resources/Schedules';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { ISchedule } from '../../schedule/types/ISchedule';
import { ConfirmActionDialog, IConfirmActionDialogInstance } from './ConfirmActionDialog';
import { ContentActionNotification } from '../../commonInfra/contentActions/ContentActionNotification';
import { ModalSize } from './ModalService';
import { ScheduleErrorsService } from './ScheduleErrors';

enum ScheduleDialogMode {
  Create,
  Edit
}

// TODO: rename to CreateOrUpdateScheduleAction
export class CreateScheduleAction {

  public static $inject = ['ConfirmActionDialog', '$translate', 'Schedules'];

  constructor(
    private ConfirmActionDialog: ConfirmActionDialog,
    private $translate: ng.translate.ITranslateService,
    private Schedules: Schedules) {

  }

  public createSchedule(context: IActionContext): IPromise<IResult> {
    return this.showDialog(
      ScheduleDialogMode.Create,
      CreateOrEditScheduleViewModelMapper.defaultSchedule(),
      context);
  }

  public editSchedule(schedule: ISchedule, context: IActionContext): IPromise<IResult> {
    return this.showDialog(
      ScheduleDialogMode.Edit,
      CreateOrEditScheduleViewModelMapper.viewModelFromSchedule(schedule),
      context);
  }

  private showDialog(
    mode: ScheduleDialogMode,
    scheduleViewModel: IScheduleViewModel,
    context: IActionContext): IPromise<IResult> {

    const dialogScope = <ICreateOrEditScheduleScope> context.scope.$new();
    dialogScope.scheduleViewModel = scheduleViewModel;

    let title: string;
    let actionName: string;
    let action: (confirmActionDialogInstance: IConfirmActionDialogInstance) => any;
    if (mode === ScheduleDialogMode.Create) {
      title = this.$translate.instant('createScheduleDialogTitle');
      actionName = this.$translate.instant('createScheduleDialog_create');
      action = (dialogInstance: IConfirmActionDialogInstance) =>
        this.submitScheduleCreate(dialogScope.scheduleViewModel, dialogInstance);
    } else {
      title = this.$translate.instant('editScheduleDialogTitle');
      actionName = this.$translate.instant('createScheduleDialog_save');
      const originalName = scheduleViewModel.name;
      action = (dialogInstance: IConfirmActionDialogInstance) =>
        this.submitScheduleUpdate(dialogScope.scheduleViewModel, originalName, dialogInstance);
    }

    return this.ConfirmActionDialog.open({
        scope: dialogScope,
        title: title,
        bodyTemplateUrl: 'createScheduleDialog.html',
        actionName: actionName,
        action: action,
        size: ModalSize.fitToContent
    }).result.finally(() => dialogScope.$destroy());
  }

  public submitScheduleCreate(scheduleViewModel: IScheduleViewModel, dialogInstance: IConfirmActionDialogInstance): IPromise<IResult> {
    const params = CreateOrEditScheduleViewModelMapper.createScheduleParamsFromViewModel(scheduleViewModel);
    return this.Schedules.create(params).then((result: IResult) => {
      if (result.errors && result.errors.length) {

        const errorMessage = ScheduleErrorsService.errorMessageFor(result.errors[0]);
        dialogInstance.showErrorMessage(errorMessage);

      } else {
        ContentActionNotification.displaySuccessToast(
          this.$translate.instant('createScheduleSuccess', { SCHEDULE_NAME: L10n.elideIfLong(params.name) })
        );
      }
      return result;
    });
  }

  public static notifyResult(result: IResult, originalScheduleName: string, newScheduleName?: string) {
    if (result.errors && result.errors.length) {

      const errorMessage = ScheduleErrorsService.errorMessageFor(result.errors[0], originalScheduleName);

      ContentActionNotification.displayFailureToast(errorMessage);

    } else {
      ContentActionNotification.displaySuccessToast(
        Messages.updateScheduleSuccess({ SCHEDULE_NAME: L10n.elideIfLong(newScheduleName || originalScheduleName)})
      );
    }
  }

  public submitScheduleUpdate(
      scheduleViewModel: IScheduleViewModel,
      originalScheduleName: string,
      dialogInstance?: IConfirmActionDialogInstance) {

    const params = CreateOrEditScheduleViewModelMapper.updateScheduleParamsFromViewModel(scheduleViewModel);
    return this.Schedules.update(params).then((result: IResult) => {

      if (result.errors && result.errors.length) {

        const errorMessage = ScheduleErrorsService.errorMessageFor(result.errors[0], originalScheduleName);

        if (dialogInstance) {
          dialogInstance.showErrorMessage(errorMessage);
        } else {
          ContentActionNotification.displayFailureToast(errorMessage);
        }

      } else {
        ContentActionNotification.displaySuccessToast(
          Messages.updateScheduleSuccess({ SCHEDULE_NAME: L10n.elideIfLong(params.name) })
        );
      }

      return result;
    });
  }

}

Module.instance().service('CreateScheduleAction', CreateScheduleAction);
